// Catálogo de brandings de cliente ("marca blanca"): colores y logo de la app para las
// empresas a las que se lo configuramos a mano. ADR-2026-10-06-branding-por-cliente.
//
// Cómo se asigna: `company_profiles.branding` guarda el slug; `null` = Atlas. Lo seteamos
// nosotros por SQL, no hay pantalla. Cómo se aplica: `aplicar.ts` escribe las variables
// CSS en `<html>` (las clases `bg-brand-primary`, `text-brand-black`, … ya las leen) y
// cambia logo, favicon y título.
//
// Para sumar un cliente: una entrada acá (los colores salen de `paletaDesdeElManual` con el
// principal y el secundario de su manual: no se eligen a mano), sus logos en
// `public/branding/<slug>/` y los tests de `catalogo.test.ts` y `contraste.test.ts` dicen si falta algo o si un color no
// tiene contraste. `atlas` tiene que ser idéntico a `:root` de `index.css`
// (`atlasEsElDefault.test.ts`): es lo que ve todo el mundo que no tiene branding.

import { paletaDesdeElManual } from './paleta';

export interface ColoresDeBranding {
  /** Botones, links, estados activos. Lleva texto blanco encima y va como texto sobre blanco. */
  primary: string;
  /** El primario al pasar el mouse: más oscuro. */
  primaryHover: string;
  /** Fondos profundos: héroes, gradientes. Texto blanco encima. */
  deep: string;
  /** El color de texto de toda la app y de las superficies oscuras. */
  black: string;
  /** Detalles: barras de progreso, badges (con texto `black`), la mascota del chat. */
  accent: string;
  /** Fondo suave del acento, con texto `black` encima. */
  accentSoft: string;
}

export interface LogosDeBranding {
  /** Isotipo a color (sidebar colapsada, pantallas chicas). */
  solo: string;
  /** Isotipo blanco, para fondos oscuros (checkout). */
  soloBlanco: string;
  /** Lockup horizontal a color (sidebar expandida). */
  horizontal: string;
  /** Lockup horizontal blanco. Si falta, se usa el isotipo blanco. */
  horizontalBlanco?: string;
  /** Alto en px del lockup en la sidebar expandida: cada logo tiene su proporción. */
  altoHorizontalEnSidebar: number;
  /** Qué variante muestra la pantalla de login. */
  login: 'solo' | 'horizontal';
}

export interface Branding {
  /** Lo que va en `company_profiles.branding` y en `?branding=`. kebab-case. */
  slug: string;
  /** Nombre de la marca: `alt` de los logos. */
  nombre: string;
  /** Nombre corto: el aviso de "instalar la app" y el encabezado del portal de facturación. */
  nombreCorto: string;
  /** Título de la pestaña del navegador. */
  titulo: string;
  colores: ColoresDeBranding;
  logos: LogosDeBranding;
  /** Ícono de la pestaña y del aviso de "instalar la app". */
  favicon: string;
  /** Manifest de la PWA: nombre e íconos de la app instalada. */
  manifest: string;
}

/** Qué variable CSS escribe cada color (las que `tailwind.config.js` mapea a `brand-*`). */
export const VARIABLE_CSS_POR_COLOR: Record<keyof ColoresDeBranding, string> = {
  primary: '--brand-primary',
  primaryHover: '--brand-primary-hover',
  deep: '--brand-deep',
  black: '--brand-black',
  accent: '--brand-accent',
  accentSoft: '--brand-accent-soft',
};

export const SLUG_ATLAS = 'atlas';

export const BRANDING_ATLAS: Branding = {
  slug: SLUG_ATLAS,
  nombre: 'Estudio Atlas',
  nombreCorto: 'Atlas',
  titulo: 'Atlas IA',
  colores: {
    primary: '#1644EB',
    primaryHover: '#1238C8',
    deep: '#01269B',
    black: '#02062B',
    accent: '#60E7F0',
    accentSoft: '#D6F8FB',
  },
  logos: {
    solo: '/images/estudio-atlas-logo-solo.svg',
    soloBlanco: '/images/estudio-atlas-logo-solo-blanco.svg',
    horizontal: '/images/estudio-atlas-logo-horizontal.svg',
    altoHorizontalEnSidebar: 19,
    login: 'solo',
  },
  favicon: '/favicon.png',
  manifest: '/manifest.json',
};

/**
 * WE ARE ("Ideas que despegan"). Manual de marca de 2021: naranja #F54D23 (el principal,
 * el saturado) y azulado #293D51 (el secundario), tipografía Andreas (no se toca la
 * tipografía de la app).
 *
 * El naranja va de primario, como el azul de Atlas: con texto blanco da 3.5:1 y AA pide
 * 4.5:1, así que `paletaDesdeElManual` lo oscurece lo justo sin cambiar el tono. El
 * azulado es el `deep` y el naranja del manual, tal cual, el acento.
 * ADR-2026-10-26-paleta-de-cliente-desde-el-manual (supersede el navy de primario de
 * ADR-2026-10-06-branding-por-cliente).
 */
export const BRANDING_WE_ARE: Branding = {
  slug: 'we-are',
  nombre: 'WE ARE',
  nombreCorto: 'WE ARE',
  titulo: 'WE ARE',
  colores: paletaDesdeElManual({ principal: '#F54D23', secundario: '#293D51' }, BRANDING_ATLAS.colores.black),
  logos: {
    solo: '/branding/we-are/logo-solo.svg',
    soloBlanco: '/branding/we-are/logo-solo-blanco.svg',
    horizontal: '/branding/we-are/logo-horizontal.svg',
    horizontalBlanco: '/branding/we-are/logo-horizontal-blanco.svg',
    // El wordmark es casi cuadrado (1.66:1): a los 19 px del lockup de Atlas quedaría de
    // 32 px de ancho.
    altoHorizontalEnSidebar: 36,
    login: 'horizontal',
  },
  favicon: '/branding/we-are/favicon.png',
  manifest: '/branding/we-are/manifest.json',
};

export const BRANDINGS: readonly Branding[] = [BRANDING_ATLAS, BRANDING_WE_ARE];

/** El branding de un slug; Atlas para nulo, vacío o desconocido. Nunca tira. */
export function brandingPorSlug(slug: string | null | undefined): Branding {
  if (!slug) return BRANDING_ATLAS;
  return BRANDINGS.find((b) => b.slug === slug) ?? BRANDING_ATLAS;
}
